import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkCreateModifiersOptions,
  BulkCreateModifiersResponse,
  BulkDeleteModifiersResponse,
  BulkUpdateModifiersOptions,
  BulkUpdateModifiersResponse,
  CountModifiersApplicationErrors,
  CountModifiersOptions,
  CountModifiersResponse,
  ItemModifierCreatedEnvelope,
  ItemModifierDeletedEnvelope,
  ItemModifierUpdatedEnvelope,
  ListModifiersOptions,
  ListModifiersResponse,
  MaskedModifier,
  Modifier,
  ModifierQuery,
  ModifiersQueryBuilder,
  QueryModifiersResponse,
  UpdateModifier,
  bulkCreateModifiers as universalBulkCreateModifiers,
  bulkDeleteModifiers as universalBulkDeleteModifiers,
  bulkUpdateModifiers as universalBulkUpdateModifiers,
  countModifiers as universalCountModifiers,
  createModifier as universalCreateModifier,
  deleteModifier as universalDeleteModifier,
  getModifier as universalGetModifier,
  listModifiers as universalListModifiers,
  queryModifiers as universalQueryModifiers,
  typedQueryModifiers as universalTypedQueryModifiers,
  updateModifier as universalUpdateModifier,
} from './restaurants-menus-v1-item-modifier-item-modifiers.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/restaurants' };

export function createModifier(
  httpClient: HttpClient
): CreateModifierSignature {
  return (modifier: NonNullablePaths<Modifier, `name`, 2>) =>
    universalCreateModifier(
      modifier,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateModifierSignature {
  /**
   * > **Note:** The Item Modifiers API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Creates an item modifier.
   *
   * To create multiple item modifiers at once, use [Bulk Create Modifiers](https://dev.wix.com/docs/rest/business-solutions/restaurants/menus/item-modifiers/bulk-create-modifiers).
   * @param - Item modifier to create.
   * @returns Created item modifier.
   */
  (modifier: NonNullablePaths<Modifier, `name`, 2>): Promise<
    NonNullablePaths<Modifier, `businessLocationIds`, 2>
  >;
}

export function getModifier(httpClient: HttpClient): GetModifierSignature {
  return (modifierId: string) =>
    universalGetModifier(
      modifierId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetModifierSignature {
  /**
   * > **Note:** The Item Modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Retrieves an item modifier by ID.
   * @param - ID of the item modifier to retrieve.
   * @returns Retrieved item modifier.
   */
  (modifierId: string): Promise<
    NonNullablePaths<Modifier, `businessLocationIds`, 2>
  >;
}

export function listModifiers(httpClient: HttpClient): ListModifiersSignature {
  return (options?: ListModifiersOptions) =>
    universalListModifiers(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListModifiersSignature {
  /**
   * > **Note:** The Item Modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Retrieves a list of up to 500 item modifiers.
   */
  (options?: ListModifiersOptions): Promise<
    NonNullablePaths<ListModifiersResponse, `modifiers`, 2>
  >;
}

export function updateModifier(
  httpClient: HttpClient
): UpdateModifierSignature {
  return (
    _id: string,
    modifier: NonNullablePaths<UpdateModifier, `revision`, 2>
  ) =>
    universalUpdateModifier(
      _id,
      modifier,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateModifierSignature {
  /**
   * > **Note:** The Item Modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Updates an item modifier.
   *
   * To update multiple item modifiers at once, use [Bulk Update Item Modifiers](https://dev.wix.com/docs/rest/business-solutions/restaurants/menus/item-modifiers/bulk-update-modifiers).
   *
   * Each time an item modifier is updated, its revision increments by 1. The existing revision must be included when updating an item modifier. This ensures you're working with the latest item modifier information, and it prevents unintended overwrites.
   * @param - Item modifier ID.
   * @returns Updated item modifier.
   */
  (
    _id: string,
    modifier: NonNullablePaths<UpdateModifier, `revision`, 2>
  ): Promise<NonNullablePaths<Modifier, `businessLocationIds`, 2>>;
}

export function bulkCreateModifiers(
  httpClient: HttpClient
): BulkCreateModifiersSignature {
  return (modifiers: Modifier[], options?: BulkCreateModifiersOptions) =>
    universalBulkCreateModifiers(
      modifiers,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateModifiersSignature {
  /**
   * > **Note:** The Item Modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Creates multiple item modifiers.
   * @param - List of item modifiers to create.
   */
  (modifiers: Modifier[], options?: BulkCreateModifiersOptions): Promise<
    NonNullablePaths<
      BulkCreateModifiersResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateModifiers(
  httpClient: HttpClient
): BulkUpdateModifiersSignature {
  return (modifiers: MaskedModifier[], options?: BulkUpdateModifiersOptions) =>
    universalBulkUpdateModifiers(
      modifiers,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateModifiersSignature {
  /**
   * > **Note:** The Item modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Updates multiple item modifiers at once.
   * Each time an item modifier is updated, its revision increments by 1. The existing revision must be included when updating the item modifier. This ensures you're working with the latest item modifier information, and prevents unintended overwrites.
   * @param - List of item modifiers to update.
   */
  (modifiers: MaskedModifier[], options?: BulkUpdateModifiersOptions): Promise<
    NonNullablePaths<
      BulkUpdateModifiersResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function deleteModifier(
  httpClient: HttpClient
): DeleteModifierSignature {
  return (modifierId: string) =>
    universalDeleteModifier(
      modifierId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteModifierSignature {
  /**
   * > **Note:** The Item modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   * Deletes an item modifier.
   * @param - ID of the item modifier to delete.
   */
  (modifierId: string): Promise<void>;
}

export function queryModifiers(
  httpClient: HttpClient
): QueryModifiersSignature {
  return () =>
    universalQueryModifiers(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryModifiersSignature {
  /**
   * Creates a query to retrieve a list of item modifiers.
   *
   * The `queryModifiers()` function builds a query to retrieve a list of item modifiers and returns a `ModifiersQueryBuilder` object.
   *
   * The returned object contains the query definition, which is used to run the query using the [`find()`](/item-modifiers/modifiers-query-builder/find) function.
   *
   * You can refine the query by chaining `ModifiersQueryBuilder` functions onto the query. `ModifiersQueryBuilder` functions enable you to filter, sort, and control the results that `queryModifiers()` returns.
   *
   * `queryModifiers()` runs with the following `ModifiersQueryBuilder` defaults, which you can override:
   *
   * * [`limit(500)`](/item-modifiers/modifiers-query-builder/limit)
   *
   * The following `ModifiersQueryBuilder` functions are supported for `queryModifiers()`. For a full description of the item modifier object, see the object returned for the [`items`](/item-modifiers/modifiers-query-result/items) property in `ModifiersQueryResult`.
   */
  (): ModifiersQueryBuilder;
}

export function typedQueryModifiers(
  httpClient: HttpClient
): TypedQueryModifiersSignature {
  return (query: ModifierQuery) =>
    universalTypedQueryModifiers(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryModifiersSignature {
  /** */
  (query: ModifierQuery): Promise<
    NonNullablePaths<QueryModifiersResponse, `modifiers`, 2>
  >;
}

export function countModifiers(
  httpClient: HttpClient
): CountModifiersSignature {
  return (options?: CountModifiersOptions) =>
    universalCountModifiers(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CountModifiersSignature {
  /**
   * > **Note:** The Item API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Retrieves the number of modifiers that match a specified filter.
   *
   * If a filter isn't passed in the request, the endpoint returns the count of all modifiers.
   */
  (options?: CountModifiersOptions): Promise<
    NonNullablePaths<CountModifiersResponse, `count`, 2> & {
      __applicationErrorsType?: CountModifiersApplicationErrors;
    }
  >;
}

export function bulkDeleteModifiers(
  httpClient: HttpClient
): BulkDeleteModifiersSignature {
  return (ids: string[]) =>
    universalBulkDeleteModifiers(
      ids,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteModifiersSignature {
  /**
   * > **Note:** The Item Modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Deletes multiple item Modifiers at once.
   * @param - Item Modifier IDs.
   */
  (ids: string[]): Promise<
    NonNullablePaths<
      BulkDeleteModifiersResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export const onItemModifierCreated = EventDefinition(
  'wix.restaurants.menus.v1.item_modifier_created',
  true,
  (event: ItemModifierCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.externalReferenceInfo.lastSyncDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<ItemModifierCreatedEnvelope>();
export const onItemModifierDeleted = EventDefinition(
  'wix.restaurants.menus.v1.item_modifier_deleted',
  true,
  (event: ItemModifierDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'undefined.externalReferenceInfo.lastSyncDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<ItemModifierDeletedEnvelope>();
export const onItemModifierUpdated = EventDefinition(
  'wix.restaurants.menus.v1.item_modifier_updated',
  true,
  (event: ItemModifierUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.externalReferenceInfo.lastSyncDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.externalReferenceInfo.lastSyncDate' },
          ],
        },
      ])
    )
)<ItemModifierUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  App,
  ApplicationError,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkCreateModifierResult,
  BulkCreateModifiersOptions,
  BulkCreateModifiersRequest,
  BulkCreateModifiersResponse,
  BulkDeleteModifiersRequest,
  BulkDeleteModifiersResponse,
  BulkDeleteModifiersResult,
  BulkItemResult,
  BulkUpdateAttachedToOrphanItemRequest,
  BulkUpdateAttachedToOrphanItemResponse,
  BulkUpdateBusinessLocationIdsPerEntityRequest,
  BulkUpdateBusinessLocationIdsPerEntityResponse,
  BulkUpdateBusinessLocationIdsPerEntityResponseBulkItemResult,
  BulkUpdateBusinessLocationIdsRequest,
  BulkUpdateBusinessLocationIdsResponse,
  BulkUpdateBusinessLocationIdsResponseBulkItemResult,
  BulkUpdateModifierResult,
  BulkUpdateModifiersOptions,
  BulkUpdateModifiersRequest,
  BulkUpdateModifiersResponse,
  CloneModifiersRequest,
  CloneModifiersResponse,
  CountModifiersOptions,
  CountModifiersRequest,
  CountModifiersResponse,
  CreateModifierRequest,
  CreateModifierResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  CustomTag,
  DeleteModifierRequest,
  DeleteModifierResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  ExternalReferenceInfo,
  File,
  GetModifierRequest,
  GetModifierResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  ItemMetadata,
  ItemModifierCreatedEnvelope,
  ItemModifierDeletedEnvelope,
  ItemModifierUpdatedEnvelope,
  ListModifiersOptions,
  ListModifiersRequest,
  ListModifiersResponse,
  MaskedModifier,
  MessageEnvelope,
  Modifier,
  ModifierIdAttachedToOrphanItem,
  ModifierIdLocationIds,
  ModifierQuerySpec,
  ModifiersQueryBuilder,
  ModifiersQueryResult,
  Page,
  Pages,
  QueryModifiersRequest,
  QueryModifiersResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  SyncContentToMultilingualRequest,
  SyncContentToMultilingualResponse,
  URI,
  URIs,
  UpdateModifier,
  UpdateModifierRequest,
  UpdateModifierResponse,
  WebhookIdentityType,
  utils,
} from './restaurants-menus-v1-item-modifier-item-modifiers.universal.js';
